import { Apartment, Delete, Edit, RemoveRedEye } from '@mui/icons-material';
import {
  numberToDisplayString,
  numberToCurrencyString,
} from 'further-ui/utils';
import {
  createLpCompanyEditRoute,
  LP_COMPANY_ADD,
} from 'adminConstants/routes';
import Page from 'components/new/Page';
import SearchField from 'further-ui/components/SearchField';
import Table, { type Column } from 'further-ui/components/Table';
import ErrorBoundary from 'components/ErrorBoundary';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Typography } from '@mui/material';
import { useDisclosure } from 'further-ui/hooks';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';

import useListCompanies from './useListCompanies';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import AvatarWithName from 'components/new/AvatarWithName';
import { useHistory } from 'react-router-dom';

const pageId = 'lp-companies';

const ListCompanies: React.FC = () => {
  const history = useHistory();
  const { vehicle } = useLayoutContext();
  const [canCreate, canEdit] = useHasPermissions([
    RolePermission.LpCreateAsset,
    RolePermission.LpEditAsset,
  ]);

  const {
    companies,
    totalCount,
    isFetching,
    filters,
    handleChangeFilters,
    pagination,
  } = useListCompanies(pageId, vehicle?._id ?? '');

  const deleteDisclosure = useDisclosure<{ id: string; name: string }>();

  const { mutate: runDelete, isPending: isDeleting } =
    api.asset.delete.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Holding deleted successfully.',
        onSuccess: () => {
          deleteDisclosure.onClose();
        },
      }),
    );

  const columns: Array<Column<(typeof companies)[number]>> = [
    {
      label: 'Holding',
      key: 'name',
      render: (holding) => (
        <AvatarWithName
          name={holding.name}
          priority="icon"
          src={holding.companyLogo}
          icon={Apartment}
          avatarSize="lg"
        />
      ),
    },
    {
      label: 'Total units acquired',
      key: 'totalNumberOfUnits',
      render: (company) => numberToDisplayString(company.totalNumberOfUnits),
      disableSorting: true,
    },
    {
      label: 'Current units held',
      key: 'currentNumberOfUnits',
      disableSorting: true,
      render: (company) => numberToDisplayString(company.currentNumberOfUnits),
    },
    {
      label: 'Initial investment',
      key: 'totalInvestmentAmountLcy',
      disableSorting: true,
      render: (company) =>
        numberToCurrencyString(company.totalInvestmentAmountLcy, {
          currency: vehicle?.baseCurrency,
        }),
    },
    {
      label: 'Hold value',
      key: 'currentHoldValueLcy',
      disableSorting: true,
      render: (company) =>
        numberToCurrencyString(company.currentHoldValueLcy, {
          currency: vehicle?.baseCurrency,
        }),
    },
  ];

  return (
    <Page
      tabTitle="Holdings: View Holdings"
      heading="Holdings"
      spacing="lg"
      headingIcon={Apartment}
      actions={[
        {
          label: 'Add holding',
          variant: 'contained',
          link: LP_COMPANY_ADD,
          hidden: !canCreate,
        },
      ]}
    >
      <PageFilters>
        <Filter size="md">
          <SearchField
            placeholder="Search by holding name"
            value={filters.searchQuery}
            onSearch={(query) => {
              handleChangeFilters({ searchQuery: query });
            }}
            autoSearchOnDebounce
            allowEmptyQuery
            fullWidth
          />
        </Filter>
      </PageFilters>
      <ErrorBoundary>
        <Table
          columns={columns}
          onRowClick={({ id }) => {
            history.push(createLpCompanyEditRoute(id));
          }}
          rows={companies}
          pagination={pagination}
          rowsTotalCount={totalCount}
          isLoading={isFetching}
          emptyMessage="No holdings found"
          actions={[
            {
              label: 'View holding',
              icon: RemoveRedEye,
              link: (company) => createLpCompanyEditRoute(company.id),
            },
            {
              label: 'Edit holding',
              icon: Edit,
              link: (company) => createLpCompanyEditRoute(company.id),
              hidden: !canEdit,
            },
            {
              label: 'Delete holding',
              icon: Delete,
              hidden: !canEdit,
              onClick: (company) => {
                deleteDisclosure.stageAction({
                  id: company.id,
                  name: company.name,
                });
              },
            },
          ]}
        />
      </ErrorBoundary>
      <ConfirmDialog
        maxWidth="sm"
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        title="Delete holding"
        content={
          <Typography variant="body2">
            Are you sure you want to delete{' '}
            <strong>{deleteDisclosure.actionProps.name}</strong> and all its
            holding classes? This cannot be undone.
          </Typography>
        }
        actions={{
          confirm: {
            label: 'Delete',
            color: 'error',
            disabled: isDeleting,
            onClick: () => {
              if (!vehicle) return;
              runDelete({
                assetId: deleteDisclosure.actionProps.id,
                vehicleId: vehicle._id,
              });
            },
          },
        }}
      />
    </Page>
  );
};

export default ListCompanies;
